import axios from 'axios';
import { API_URL } from '../../config/api';
import { readRemindersFromRealm, upsertRealmDocs, realmAvailable } from '../../realm';
import {
  writePendingReminderToRealm,
  applyPendingReminderUpdate,
  queuePendingReminderDelete,
  markReminderSynced,
  overlayPendingReminders,
  isNetworkFailure,
} from '../../realm/outboundQueue';
import { getTodayDateStringInTimezone, resolveReminderApiId, toReminderApiDateParam } from './reminderUtils';

const notifyCalendarChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('workloop:calendar-changed'));
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
  };
};

const getAuthUserId = () => {
  try {
    const raw = localStorage.getItem('workloop_user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.id || null;
  } catch {
    return null;
  }
};

const shouldReturnQueued = (optimistic, error) =>
  Boolean(optimistic) && (
    isNetworkFailure(error)
    || (typeof navigator !== 'undefined' && navigator.onLine === false)
  );

const hydrateReminders = async (list) => overlayPendingReminders(Array.isArray(list) ? list : []);

export const fetchReminders = async (targetUserId, { forceNetwork = false } = {}) => {
  if (!targetUserId && !forceNetwork && (await realmAvailable())) {
    const cached = await readRemindersFromRealm();
    if (cached?.length) return hydrateReminders(cached);
  }

  const url = targetUserId
    ? `${API_URL}/reminders?targetUserId=${encodeURIComponent(targetUserId)}`
    : `${API_URL}/reminders`;
  try {
    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });
    const list = Array.isArray(response.data) ? response.data : [];
    if (!targetUserId) {
      upsertRealmDocs('Reminder', list);
    }
    return hydrateReminders(list);
  } catch (err) {
    if (!targetUserId) {
      const cached = await readRemindersFromRealm();
      if (cached && cached.length) return hydrateReminders(cached);
      const queued = await hydrateReminders([]);
      if (queued.length) return queued;
    }
    throw err;
  }
};

export const fetchReminderById = async (id) => {
  const response = await axios.get(`${API_URL}/reminders/${id}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createReminder = async (data) => {
  const local = await writePendingReminderToRealm({
    data,
    userId: getAuthUserId(),
  });
  if (typeof navigator !== 'undefined' && navigator.onLine === false && local) {
    return local;
  }
  try {
    const response = await axios.post(`${API_URL}/reminders`, data, {
      headers: getAuthHeaders(),
    });
    if (local?.id) {
      await markReminderSynced(local.id, response.data);
    } else {
      upsertRealmDocs('Reminder', response.data);
    }
    return response.data;
  } catch (err) {
    if (shouldReturnQueued(local, err)) return local;
    throw err;
  }
};

export const updateReminder = async (id, data) => {
  const local = await applyPendingReminderUpdate({
    reminderId: id,
    patch: data,
    outboundOp: 'update',
  });
  if (typeof navigator !== 'undefined' && navigator.onLine === false && local) {
    return local;
  }
  try {
    const response = await axios.put(`${API_URL}/reminders/${id}`, data, {
      headers: getAuthHeaders(),
    });
    await markReminderSynced(id, response.data);
    return response.data;
  } catch (err) {
    if (shouldReturnQueued(local, err)) return local;
    throw err;
  }
};

export const deleteReminder = async (idOrReminder) => {
  const reminder = typeof idOrReminder === 'object' && idOrReminder
    ? idOrReminder
    : { id: idOrReminder };
  const id = reminder.id || reminder._id;
  const local = await queuePendingReminderDelete(reminder);
  if (typeof navigator !== 'undefined' && navigator.onLine === false && local) {
    return id;
  }
  try {
    await axios.delete(`${API_URL}/reminders/${id}`, {
      headers: getAuthHeaders(),
    });
    await markReminderSynced(id, null);
    return id;
  } catch (err) {
    if (shouldReturnQueued(local, err)) return id;
    throw err;
  }
};

export const markReminderCompleteForToday = async (reminderOrId, dateStr) => {
  const reminder = typeof reminderOrId === 'object' && reminderOrId
    ? reminderOrId
    : { id: reminderOrId };
  const id = resolveReminderApiId(reminder);
  const dayStr = toReminderApiDateParam(dateStr) || getTodayDateStringInTimezone(reminder.timezone);
  const completedDates = Array.from(new Set([
    ...(Array.isArray(reminder.completedDates) ? reminder.completedDates : []),
    dayStr,
  ]));
  const skippedDates = (Array.isArray(reminder.skippedDates) ? reminder.skippedDates : [])
    .filter((date) => date !== dayStr);
  const local = await applyPendingReminderUpdate({
    reminderId: id,
    existing: reminder,
    patch: { completedDates, skippedDates },
    outboundOp: 'complete-today',
    outboundRequest: { date: dayStr },
  });
  if (!id || String(id).startsWith('pending-') || (typeof navigator !== 'undefined' && navigator.onLine === false && local)) {
    notifyCalendarChanged();
    return local;
  }
  try {
    const response = await axios.post(`${API_URL}/reminders/${encodeURIComponent(id)}/complete-today`, {}, {
      headers: getAuthHeaders(),
      params: { date: dayStr },
    });
    await markReminderSynced(id, response.data);
    notifyCalendarChanged();
    return response.data;
  } catch (err) {
    if (shouldReturnQueued(local, err)) {
      notifyCalendarChanged();
      return local;
    }
    throw err;
  }
};

export const skipReminderForToday = async (reminderOrId, dateStr) => {
  const reminder = typeof reminderOrId === 'object' && reminderOrId
    ? reminderOrId
    : { id: reminderOrId };
  let id = resolveReminderApiId(reminder);
  const dayStr = toReminderApiDateParam(dateStr) || getTodayDateStringInTimezone(reminder.timezone);
  const skippedDates = Array.from(new Set([
    ...(Array.isArray(reminder.skippedDates) ? reminder.skippedDates : []),
    dayStr,
  ]));
  const completedDates = (Array.isArray(reminder.completedDates) ? reminder.completedDates : [])
    .filter((date) => date !== dayStr);
  const local = await applyPendingReminderUpdate({
    reminderId: id,
    existing: reminder,
    patch: { skippedDates, completedDates, snoozeUntil: null },
    outboundOp: 'skip-today',
    outboundRequest: { date: dayStr },
  });
  if (!id || String(id).startsWith('pending-') || (typeof navigator !== 'undefined' && navigator.onLine === false && local)) {
    notifyCalendarChanged();
    return local;
  }
  const postSkip = (reminderId) => axios.post(`${API_URL}/reminders/${encodeURIComponent(reminderId)}/skip-today`, {}, {
    headers: getAuthHeaders(),
    params: { date: dayStr },
  });
  try {
    let response;
    try {
      response = await postSkip(id);
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
      const list = await fetchReminders(undefined, { forceNetwork: true });
      const match = (Array.isArray(list) ? list : []).find((item) => {
        const itemId = resolveReminderApiId(item);
        if (itemId && itemId === id) return true;
        if (reminder.title && item?.title === reminder.title) {
          const sameTz = !reminder.timezone || item?.timezone === reminder.timezone;
          const sameTime = !reminder.recurrence?.time || item?.recurrence?.time === reminder.recurrence?.time;
          return sameTz && sameTime;
        }
        return false;
      });
      const retryId = resolveReminderApiId(match);
      if (!retryId) throw err;
      id = retryId;
      response = await postSkip(id);
    }
    await markReminderSynced(id, response.data);
    notifyCalendarChanged();
    return response.data;
  } catch (err) {
    if (shouldReturnQueued(local, err)) {
      notifyCalendarChanged();
      return local;
    }
    throw err;
  }
};

const applyLocalTaskToggle = (reminder, taskId, dayStr) => {
  const tasks = Array.isArray(reminder?.tasks) ? reminder.tasks : [];
  const taskDays = Array.isArray(reminder?.taskDays) ? reminder.taskDays.map((day) => ({
    ...day,
    completedTaskIds: Array.isArray(day?.completedTaskIds) ? [...day.completedTaskIds] : [],
  })) : [];
  let day = taskDays.find((item) => item.date === dayStr);
  if (!day) {
    day = { date: dayStr, completedTaskIds: [] };
    taskDays.push(day);
  }
  const completedIds = new Set(day.completedTaskIds);
  const allIds = tasks.map((task) => task?.id).filter(Boolean);
  const wasAllDone = allIds.length > 0 && allIds.every((id) => completedIds.has(id));
  if (completedIds.has(taskId)) completedIds.delete(taskId);
  else completedIds.add(taskId);
  day.completedTaskIds = Array.from(completedIds);
  const allDone = allIds.length > 0 && allIds.every((id) => completedIds.has(id));
  let completedDates = Array.isArray(reminder?.completedDates) ? [...reminder.completedDates] : [];
  let skippedDates = Array.isArray(reminder?.skippedDates) ? [...reminder.skippedDates] : [];
  let enabled = reminder?.enabled;
  if (allDone) {
    if (!completedDates.includes(dayStr)) completedDates.push(dayStr);
    skippedDates = skippedDates.filter((date) => date !== dayStr);
  } else if (wasAllDone) {
    completedDates = completedDates.filter((date) => date !== dayStr);
    enabled = true;
  }
  return {
    ...reminder,
    taskDays,
    completedDates,
    skippedDates,
    enabled,
  };
};

export const toggleReminderTask = async (reminderOrId, taskId, dateStr) => {
  const reminder = typeof reminderOrId === 'object' && reminderOrId
    ? reminderOrId
    : { id: reminderOrId };
  const id = resolveReminderApiId(reminder);
  const dayStr = toReminderApiDateParam(dateStr) || getTodayDateStringInTimezone(reminder.timezone);
  const patched = applyLocalTaskToggle(reminder, taskId, dayStr);
  const local = await applyPendingReminderUpdate({
    reminderId: id,
    existing: reminder,
    patch: {
      taskDays: patched.taskDays,
      completedDates: patched.completedDates,
      skippedDates: patched.skippedDates,
      enabled: patched.enabled,
    },
    outboundOp: 'toggle-task',
    outboundRequest: { taskId, date: dayStr },
  });
  if (typeof navigator !== 'undefined' && navigator.onLine === false && local) {
    notifyCalendarChanged();
    return local;
  }
  try {
    const response = await axios.post(`${API_URL}/reminders/${id}/tasks/${encodeURIComponent(taskId)}/toggle`, {}, {
      headers: getAuthHeaders(),
      params: { date: dayStr },
    });
    await markReminderSynced(id, response.data);
    notifyCalendarChanged();
    return response.data;
  } catch (err) {
    if (shouldReturnQueued(local, err)) {
      notifyCalendarChanged();
      return local;
    }
    throw err;
  }
};

export const snoozeReminder = async (reminderOrId, minutes) => {
  const reminder = typeof reminderOrId === 'object' && reminderOrId
    ? reminderOrId
    : { id: reminderOrId };
  const id = resolveReminderApiId(reminder);
  const response = await axios.post(`${API_URL}/reminders/${encodeURIComponent(id)}/snooze`, { minutes }, {
    headers: getAuthHeaders(),
  });
  if (response.data) {
    upsertRealmDocs('Reminder', response.data);
  }
  return response.data;
};

export const fetchRemindersForMe = async () => {
  const response = await axios.get(`${API_URL}/reminders/for-me`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const acceptReminder = async (id) => {
  const response = await axios.post(`${API_URL}/reminders/${id}/accept`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const rejectReminder = async (id) => {
  const response = await axios.post(`${API_URL}/reminders/${id}/reject`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const revokeReminder = async (id) => {
  const response = await axios.post(`${API_URL}/reminders/${id}/revoke`, {}, {
    headers: getAuthHeaders(),
  });
  return response.data;
};
